// src/pages/admin/AccountSetting.js
import { useState, useEffect } from 'react';
import axios from '../../api/axios';
import {
  Box,
  Button,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Edit, Delete, Settings, Save } from '@mui/icons-material';
import Download from '@mui/icons-material/Download';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import './AccountSetting.css';

function AccountSetting() {
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editedAdmin, setEditedAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    idNumber: '',
    position: '',
    role: 'admin',
  });
  const [filterName, setFilterName] = useState('');
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const confirmDeleteAdmin = (id, name) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete admin: ${name}?`,
    );
    if (confirmed) {
      handleDeleteAdmin(id);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get('/api/admins');
      setAdmins(response.data);
      setFilteredAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const handleToggleActive = async (id, active) => {
    try {
      await axios.put(`/api/admins/${id}/toggle-active`);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin:', error);
    }
  };

  // Function to handle modal open/close
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDetailsOpen = (admin) => {
    setSelectedAdmin(admin);
    setEditedAdmin({ ...admin, password: '' });
    setEditMode(false);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setEditMode(false);
  };

  // Function to toggle edit mode
  const handleToggleEdit = () => {
    setEditMode(!editMode);
  };

  const handleSaveEdit = async () => {
    try {
      const updatedData = { ...editedAdmin };

      // If password is empty or not changed, remove it to prevent double hashing
      if (!updatedData.password || updatedData.password.trim() === '') {
        delete updatedData.password;
      }

      await axios.put(`/api/admins/${editedAdmin._id}`, updatedData);
      fetchAdmins();
      setEditMode(false);
    } catch (error) {
      console.error('Error updating admin:', error);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedAdmin({ ...editedAdmin, [field]: value });
  };

  const handleAddAdmin = async () => {
    if (
      newAdmin.name &&
      newAdmin.email &&
      newAdmin.password &&
      newAdmin.idNumber &&
      newAdmin.position
    ) {
      try {
        await axios.post('/api/admins', {
          name: newAdmin.name,
          email: newAdmin.email,
          password: newAdmin.password,
          idNumber: newAdmin.idNumber,
          position: newAdmin.position,
        });
        fetchAdmins();
        setNewAdmin({
          name: '',
          email: '',
          password: '',
          idNumber: '',
          position: '',
        });
        handleClose();
      } catch (error) {
        console.error('Error adding admin:', error);
      }
    }
  };

  const handleDeleteAdmin = async (id) => {
    try {
      await axios.delete(`/api/admins/${id}`);
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value.toLowerCase();
    setFilterName(value);
    setFilteredAdmins(
      admins.filter((admin) => admin.name.toLowerCase().includes(value)),
    );
  };

  // Fetch the superadmin user
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const res = await axios.get('/api/auth/me', { withCredentials: true });
        setUser(res.data);
      } catch (err) {
        console.error('Error fetching current user:', err);
      } finally {
        setCheckingAuth(false);
      }
    };
    getCurrentUser();
  }, []);

  if (checkingAuth) return <div>Loading...</div>;

  if (!user || user.role !== 'superadmin') {
    return (
      <div className="unauthorized">🚫 Access denied. Super Admins only.</div>
    );
  }

  const handleExportLogs = async () => {
    try {
      const res = await axios.get('/api/logs/export');
      const logsToExport = res.data;

      const headers = ['Performed By', 'Entity', 'Action Details', 'Date'];
      const rows = logsToExport.map((log) => [
        log.performedBy,
        log.entity,
        log.details,
        new Date(log.createdAt).toLocaleString(),
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
        )
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `system_logs_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export logs:', err);
      alert('Failed to export logs. See console for details.');
    }
  };

  const handleDeleteLogs = async () => {
    const confirmed = window.confirm(
      '⚠️ Are you sure you want to delete ALL system logs? This action cannot be undone.',
    );
    if (!confirmed) return;

    try {
      await axios.delete('/api/logs');
      alert('✅ Logs successfully deleted.');
    } catch (err) {
      console.error('Failed to delete logs:', err);
      alert('Failed to delete logs. See console for details.');
    }
  };

  return (
    <div display="flex" className="container">
      {/* Main Content */}
      <Box component="main" className="main-container">
        <div className="content-wrapper">
          <Box p={3}>
            <div container spacing={2} alignItems="center" columns={12}>
              {/* Manage Users Section */}
              <div className="admin-section">
                <h3>Manage Admins</h3>

                <div className="admin-controls">
                  <input
                    type="text"
                    placeholder="Filter by Name"
                    value={filterName}
                    onChange={handleFilterChange}
                    className="admin-input"
                  />

                  <button className="btn btn-outline" onClick={handleOpen}>
                    Add Admin
                  </button>
                </div>

                <div className="log-controls">
                  <button
                    className="btn btn-primary"
                    onClick={handleExportLogs}
                  >
                    <Download
                      style={{ marginRight: '5px', verticalAlign: 'middle' }}
                    />
                    Download Logs
                  </button>
                  <button className="btn btn-danger" onClick={handleDeleteLogs}>
                    <Delete
                      style={{ marginRight: '5px', verticalAlign: 'middle' }}
                    />
                    Delete All Logs
                  </button>
                </div>
              </div>

              {/* User List Table */}
              <Grid item xs={12}>
                <CardContent>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '150px' }}>
                            <b>Name</b>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '250px',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            <b>Email</b>
                          </TableCell>
                          <TableCell align="center" sx={{ width: '100px' }}>
                            <b>Active</b>
                          </TableCell>
                          <TableCell align="center" sx={{ width: '150px' }}>
                            <b>Actions</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAdmins.map((admin) => (
                          <TableRow key={admin._id}>
                            <TableCell>{admin.name}</TableCell>
                            <TableCell
                              sx={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                            >
                              {admin.email}
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                checked={admin.active}
                                onChange={() =>
                                  handleToggleActive(admin._id, admin.active)
                                }
                                color="primary"
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                    {
                                      backgroundColor: '#272974',
                                    },
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                display="flex"
                                justifyContent="center"
                                gap={1}
                              >
                                <IconButton
                                  sx={{ color: 'grey' }}
                                  onClick={() => handleDetailsOpen(admin)}
                                >
                                  <Settings />
                                </IconButton>
                                <IconButton
                                  sx={{ color: 'red' }}
                                  color="error"
                                  onClick={() =>
                                    confirmDeleteAdmin(admin._id, admin.name)
                                  }
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Grid>
            </div>
          </Box>
        </div>
      </Box>

      {/* Add User Modal */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add New Admin</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            margin="dense"
            value={newAdmin.name}
            onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            margin="dense"
            value={newAdmin.email}
            onChange={(e) =>
              setNewAdmin({ ...newAdmin, email: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="dense"
            value={newAdmin.password}
            onChange={(e) =>
              setNewAdmin({ ...newAdmin, password: e.target.value })
            }
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                  sx={{ marginRight: '5px' }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          <TextField
            fullWidth
            label="ID Number"
            variant="outlined"
            margin="dense"
            value={newAdmin.idNumber}
            onChange={(e) =>
              setNewAdmin({ ...newAdmin, idNumber: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Position"
            variant="outlined"
            margin="dense"
            value={newAdmin.position}
            onChange={(e) =>
              setNewAdmin({ ...newAdmin, position: e.target.value })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newAdmin.role === 'superadmin'}
                onChange={(e) =>
                  setNewAdmin({
                    ...newAdmin,
                    role: e.target.checked ? 'superadmin' : 'admin',
                  })
                }
              />
            }
            label="SuperAdmin"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleAddAdmin} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={handleDetailsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5' }}>Account Details</DialogTitle>
        <DialogContent dividers>
          {selectedAdmin && (
            <List>
              <ListItem>
                {editMode ? (
                  <TextField
                    fullWidth
                    label="Name"
                    value={editedAdmin.name}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    variant="outlined"
                    margin="dense"
                  />
                ) : (
                  <ListItemText
                    primary="Name"
                    secondary={selectedAdmin.name}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                )}
              </ListItem>
              <Divider />
              <ListItem>
                {editMode ? (
                  <TextField
                    fullWidth
                    label="Email"
                    value={editedAdmin.email}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    variant="outlined"
                    margin="dense"
                  />
                ) : (
                  <ListItemText
                    primary="Email"
                    secondary={selectedAdmin.email}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                )}
              </ListItem>
              <ListItem>
                {editMode ? (
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={editedAdmin.password || ''}
                    onChange={(e) =>
                      handleEditChange('password', e.target.value)
                    }
                    variant="outlined"
                    margin="dense"
                    placeholder="Leave blank to keep current password"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                          sx={{ marginRight: '5px' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                ) : (
                  <ListItemText
                    primary="Password"
                    secondary="********"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                )}
              </ListItem>

              <Divider />

              <Divider />
              <ListItem>
                {editMode ? (
                  <TextField
                    fullWidth
                    label="ID Number"
                    value={editedAdmin.idNumber}
                    onChange={(e) =>
                      handleEditChange('idNumber', e.target.value)
                    }
                    variant="outlined"
                    margin="dense"
                  />
                ) : (
                  <ListItemText
                    primary="ID Number"
                    secondary={selectedAdmin.idNumber}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                )}
              </ListItem>
              <Divider />
              <ListItem>
                {editMode ? (
                  <TextField
                    fullWidth
                    label="Position"
                    value={editedAdmin.position}
                    onChange={(e) =>
                      handleEditChange('position', e.target.value)
                    }
                    variant="outlined"
                    margin="dense"
                  />
                ) : (
                  <ListItemText
                    primary="Position"
                    secondary={selectedAdmin.position}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                )}
              </ListItem>
              <Divider />
              <ListItem>
                {editMode ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editedAdmin.role === 'superadmin'}
                        onChange={(e) =>
                          setEditedAdmin({
                            ...editedAdmin,
                            role: e.target.checked ? 'superadmin' : 'admin',
                          })
                        }
                      />
                    }
                    label="SuperAdmin"
                  />
                ) : (
                  <ListItemText
                    primary="Role"
                    secondary={selectedAdmin.role}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                )}
              </ListItem>
              <Divider />
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            startIcon={editMode ? <Save /> : <Edit />}
            onClick={editMode ? handleSaveEdit : handleToggleEdit}
            sx={{ mr: 1 }}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
          <Button onClick={handleDetailsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AccountSetting;
